/*
 * discover-lx1-runtime.js — native behaviours for the Discover Moasure LX1 sections (sections/discover-lx1-*.liquid).
 * Loaded (deferred) by snippets/discover-lx1-head.liquid.
 *
 * Native replacement for Instant's `instant-core.min.js` + Swiper on this page: the industries slider (bullets,
 * prev/next, drag), the single-open FAQ accordion, the two logo tickers, the sticky "Shop Moasure LX1" bar and the
 * early-access waitlist pop-up (open-overlay buttons, close, backdrop, Escape). It
 * reads the SAME `data-instant-*` attributes and slider-params JSON the markup carries and drives the SAME classes
 * and state attributes the stylesheet responds to, so the page looks and behaves identically with or without the
 * Instant app.
 *
 * COEXISTENCE: while the Instant app embed is installed, instant-core initialises this markup itself. This runtime
 * STANDS DOWN whenever instant-core actually ran and only takes over when it did not (app removed, or blocked). The
 * app embed sets window.Instant.initialized even when instant-core never loads — that flag is never used as evidence.
 * Sets window.__discoverLx1Mode = 'instant' | 'native' and window.__discoverLx1NativeReady = true once wired.
 *
 * Derived from assets/landscaper-runtime.js (sliders, accordions, overlays, scroll triggers — proven on the Landscaper page)
 * and assets/home-hero-runtime.js (tickers). The first-party components (hero video, book-a-demo popup, call
 * button, feature videos) have their own files and run in both modes.
 *
 * Defensive throughout (never throws), idempotent (global + per-element guards).
 */
(function () {
  'use strict';
  if (window.__discoverLx1Init) return;
  window.__discoverLx1Init = true;

  var ROOT_SEL = '[data-discover-lx1-native]';
  var DISABLED = 'instant-slider-button-disabled';

  function $all(sel, ctx) { try { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); } catch (e) { return []; } }
  function safe(fn) { return function () { try { return fn.apply(this, arguments); } catch (e) { /* swallow */ } }; }
  function closest(el, sel) { while (el && el.nodeType === 1) { if (el.matches && el.matches(sel)) return el; el = el.parentElement; } return null; }
  function bound(el, key) { key = key || '__dlBound'; if (!el || el[key]) return true; el[key] = true; return false; }
  function kids(el, cls) {
    var out = [];
    if (!el) return out;
    for (var i = 0; i < el.children.length; i++) if (!cls || el.children[i].classList.contains(cls)) out.push(el.children[i]);
    return out;
  }
  function num(v) { var n = parseFloat(v); return isNaN(n) ? 0 : n; }

  /* ================= sliders (the industries slider) ================= */
  var mains = {};
  var allApis = [];

  function readParams(el) {
    try { var s = document.getElementById('instant-slider-' + el.id + '-params'); return s ? JSON.parse(s.textContent) : null; } catch (e) { return null; }
  }
  function breakpoint(params) {
    if (!params || !params.breakpoints) return {};
    var w = window.innerWidth, best = null, bestK = -1;
    for (var k in params.breakpoints) {
      var n = parseInt(k, 10);
      if (w >= n && n > bestK) { bestK = n; best = params.breakpoints[k]; }
    }
    return best || {};
  }

  function initSlider(el) {
    if (bound(el)) return null;
    var wrapper = el.querySelector('.instant-slider-wrapper');
    if (!wrapper) return null;
    var slides = kids(wrapper, 'instant-slider-slide');
    if (!slides.length) return null;

    var params = readParams(el) || {};
    var container = closest(el, '.instant-slider-container') || el.parentElement || document;
    var sid = el.getAttribute('data-instant-slider-id');
    var pagCfg = params.pagination || null;
    var dynamic = !!(pagCfg && pagCfg.dynamicBullets);
    var watchOverflow = params.watchOverflow !== false;

    var idx = 0, gap = 0, spv = 1, auto = false, centered = false;
    // Swiper vocabulary: `slidesGrid` = each slide's offset; `snapGrid` = the reachable positions (slide offsets that
    // fit, plus the maximum translate when it sits more than 1px past the last of them). Bullets and translate use
    // the snap index; the slide-active class uses the slide index (the last slide whose offset ≤ the translate).
    var slidesGrid = [0], snapGrid = [0], snaps = 1, maxIdx = 0, activeSlide = 0, slideW = 0, locked = false, dormant = true;
    var pagEl = null, bulletClass = '', bullets = [];

    el.classList.add('instant-slider-initialized', 'instant-slider-horizontal', 'instant-slider-backface-hidden');
    if (params.watchSlidesProgress) el.classList.add('instant-slider-watch-progress');
    el.setAttribute('data-instant-slider-initialized', 'true');

    function navEls(sel) {
      if (!sel) return [];
      var els = $all(sel, container);
      return els.length ? els : $all(sel, document);
    }
    function offset(i) { return snapGrid[Math.max(0, Math.min(i, snapGrid.length - 1))] || 0; }

    // Resolve the pagination element and learn the hashed bullet class from the markup's seed bullet. Must run
    // BEFORE anything empties that element, or re-rendered bullets would lose the hash the stylesheet keys on.
    function resolvePag() {
      if (pagEl || !pagCfg || !pagCfg.el || pagCfg.enabled === false) return pagEl;
      pagEl = container.querySelector(pagCfg.el) || document.querySelector(pagCfg.el);
      if (!pagEl) return null;
      var first = pagEl.firstElementChild;
      if (first) {
        bulletClass = first.className.split(/\s+/).filter(function (c) {
          return c && c.indexOf('instant-slider-pagination-bullet') !== 0;
        }).join(' ');
      }
      pagEl.classList.add('instant-slider-pagination-clickable', 'instant-slider-pagination-bullets', 'instant-slider-pagination-horizontal');
      if (dynamic) pagEl.classList.add('instant-slider-pagination-bullets-dynamic');
      return pagEl;
    }

    function layout() {
      var b = breakpoint(params);
      var rawSpv = b.slidesPerView;
      auto = rawSpv === 'auto';
      spv = auto ? 1 : (rawSpv || 1);
      gap = b.spaceBetween || 0;
      centered = !!b.centeredSlides;

      // Swiper sizes slides against the container's CONTENT box: clientWidth minus its horizontal padding
      // (measured on this page's workflow slider at 1440: Swiper wrote 330px where the full clientWidth gave 342px,
      // and the taller images shifted everything below by 26px).
      var csEl = getComputedStyle(el);
      var W = el.clientWidth - num(csEl.paddingLeft) - num(csEl.paddingRight);
      // A slider inside a hidden responsive variant (the mobile feature slider at desktop widths) has
      // clientWidth 0 — Swiper leaves those alone until they become visible, but it DOES empty the markup's seed
      // bullet. Mirror both, and re-run layout when the box appears (ResizeObserver).
      dormant = W <= 0;
      resolvePag();
      if (dormant) {
        if (pagEl && pagEl.children.length) { pagEl.innerHTML = ''; bullets = []; }
        return;
      }

      var i, prevSnaps = snaps;
      // slidesPerView:"auto" → the stylesheet owns the slide width (Swiper writes no inline width);
      // numeric → Swiper's formula wins (measured on the M2 page: 512px at 1024 where the CSS said 511.672px).
      if (auto) {
        for (i = 0; i < slides.length; i++) { slides[i].style.width = ''; slides[i].style.marginRight = gap ? gap + 'px' : ''; }
      } else {
        slideW = spv === 1 ? W : (W - (spv - 1) * gap) / spv;
        for (i = 0; i < slides.length; i++) {
          slides[i].style.width = slideW + 'px';
          slides[i].style.marginRight = gap ? gap + 'px' : '';
        }
      }
      // Geometry the way Swiper computes it (verified against el.swiper.snapGrid on the live page at 390/1440):
      //   numeric spv → slide size is the formula width, gap = spaceBetween (written as inline margin-right)
      //   auto        → slide size = measured width + the stylesheet's own margins, gap = spaceBetween (0 here)
      //   virtualSize = Σ(size + gap) − gap ; maxTranslate = virtualSize − container size
      //   snapGrid = every slide offset ≤ maxTranslate, plus maxTranslate itself when it is >1px past the last one
      var pos = 0; slidesGrid = [];
      for (i = 0; i < slides.length; i++) {
        var scs = getComputedStyle(slides[i]);
        var size = auto ? slides[i].offsetWidth + num(scs.marginLeft) + num(scs.marginRight) : slides[i].offsetWidth;
        slidesGrid.push(pos);
        pos += size + gap;
      }
      var virtualSize = Math.max(0, pos - gap);
      var maxTranslate = Math.max(0, virtualSize - W);
      slideW = slides[0] ? slides[0].offsetWidth : 0;
      if (centered) {
        snapGrid = slidesGrid.slice();
      } else {
        snapGrid = slidesGrid.filter(function (o) { return o <= maxTranslate; });
        if (!snapGrid.length) snapGrid = [0];
        if (Math.floor(maxTranslate) - Math.floor(snapGrid[snapGrid.length - 1]) > 1) snapGrid.push(maxTranslate);
      }
      snaps = snapGrid.length;
      maxIdx = snaps - 1;

      // watchOverflow: nothing to scroll → Swiper locks nav + pagination (the workflow slider at ≥1025px: 4 slides, 4 per view)
      locked = watchOverflow && maxIdx <= 0;
      el.classList.toggle('instant-slider-locked', locked);
      var nav = params.navigation;
      if (nav) {
        navEls(nav.prevEl).concat(navEls(nav.nextEl)).forEach(function (b2) { b2.classList.toggle('instant-slider-button-lock', locked); });
      }

      if (snaps !== prevSnaps || !bullets.length) renderBullets();
      if (idx > maxIdx) idx = maxIdx;
    }

    function updateNav() {
      var nav = params.navigation;
      if (!nav) return;
      navEls(nav.prevEl).forEach(function (b) { b.classList.toggle(DISABLED, idx <= 0); });
      navEls(nav.nextEl).forEach(function (b) { b.classList.toggle(DISABLED, idx >= maxIdx); });
    }

    function updateBullets() {
      for (var i = 0; i < bullets.length; i++) {
        var b = bullets[i], d = i - idx;
        b.classList.toggle('instant-slider-pagination-bullet-active', i === idx);
        if (!dynamic) continue;
        b.classList.toggle('instant-slider-pagination-bullet-active-main', d === 0);
        b.classList.toggle('instant-slider-pagination-bullet-active-next', d === 1);
        b.classList.toggle('instant-slider-pagination-bullet-active-next-next', d === 2);
        b.classList.toggle('instant-slider-pagination-bullet-active-prev', d === -1);
        b.classList.toggle('instant-slider-pagination-bullet-active-prev-prev', d === -2);
      }
    }

    function renderBullets() {
      if (!resolvePag()) return;
      pagEl.classList.toggle('instant-slider-pagination-lock', locked);
      pagEl.innerHTML = '';
      bullets = [];
      for (var i = 0; i < snaps; i++) {
        var b = document.createElement('span');
        b.className = 'instant-slider-pagination-bullet' + (bulletClass ? ' ' + bulletClass : '');
        b.addEventListener('click', safe((function (k) { return function () { go(k, true); }; })(i)));
        pagEl.appendChild(b);
        bullets.push(b);
      }
      updateBullets();
    }

    function go(i, animate) {
      i = Math.max(0, Math.min(i, maxIdx));
      idx = i;
      var t = offset(i);
      wrapper.style.transitionDuration = animate === false ? '0ms' : '300ms';
      wrapper.style.transform = 'translate3d(-' + t + 'px, 0px, 0px)';
      activeSlide = 0;
      for (var k = 0; k < slidesGrid.length; k++) if (slidesGrid[k] <= t + 1) activeSlide = k;
      for (var n = 0; n < slides.length; n++) {
        slides[n].classList.toggle('instant-slider-slide-active', n === activeSlide);
        slides[n].classList.toggle('instant-slider-slide-prev', n === activeSlide - 1);
        slides[n].classList.toggle('instant-slider-slide-next', n === activeSlide + 1);
      }
      updateNav(); updateBullets();
    }

    var nav0 = params.navigation;
    if (nav0) {
      navEls(nav0.nextEl).forEach(function (b) { if (!bound(b, '__dlNav')) b.addEventListener('click', safe(function (e) { e.preventDefault(); go(idx + 1, true); })); });
      navEls(nav0.prevEl).forEach(function (b) { if (!bound(b, '__dlNav')) b.addEventListener('click', safe(function (e) { e.preventDefault(); go(idx - 1, true); })); });
    }

    /* drag / swipe — pointer tracked on the DOCUMENT, never captured: setPointerCapture retargets the compatibility
       click to the slider element, so a plain tap on a button inside a slide would never reach it (measured on
       the Landscaper page, whose in-slide overlay buttons stayed dead until this changed). */
    var pid = null, startX = 0, startT = 0, base = 0, dragging = false;
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
    function onMove(e) {
      if (pid === null || e.pointerId !== pid) return;
      var dx = e.clientX - startX;
      if (!dragging && Math.abs(dx) <= 5) return;
      dragging = true;
      wrapper.style.transitionDuration = '0ms';
      wrapper.style.userSelect = 'none';
      var p = base + dx, min = -offset(maxIdx), max = 0;
      if (p > max) p = max + (p - max) * 0.3;
      if (p < min) p = min + (p - min) * 0.3;
      wrapper.style.transform = 'translate3d(' + p + 'px, 0px, 0px)';
    }
    function onUp(e) {
      if (pid === null || (e && e.pointerId !== undefined && e.pointerId !== pid)) return;
      document.removeEventListener('pointermove', onMoveSafe); document.removeEventListener('pointerup', onUpSafe); document.removeEventListener('pointercancel', onUpSafe);
      pid = null;
      wrapper.style.userSelect = '';
      if (!dragging) return;
      dragging = false;
      var dx = (e && e.clientX !== undefined ? e.clientX : startX) - startX;
      var dt = Math.max(1, Date.now() - startT);
      if (Math.abs(dx) > slideW * 0.2 || Math.abs(dx) / dt > 0.3) go(idx - (dx > 0 ? 1 : -1), true); else go(idx, true);
      el.__dlSuppressClick = true;
      setTimeout(function () { el.__dlSuppressClick = false; }, 300);
    }
    var onMoveSafe = safe(onMove), onUpSafe = safe(onUp);
    el.addEventListener('pointerdown', safe(function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (locked) return;
      pid = e.pointerId; startX = e.clientX; startT = Date.now(); base = -offset(idx); dragging = false;
      document.addEventListener('pointermove', onMoveSafe); document.addEventListener('pointerup', onUpSafe); document.addEventListener('pointercancel', onUpSafe);
    }));
    el.addEventListener('click', function (e) { if (el.__dlSuppressClick) { e.preventDefault(); e.stopPropagation(); } }, true);

    var api = {
      el: el, go: go, layout: layout,
      index: function () { return idx; }, isDormant: function () { return dormant; },
      relayout: function () { var was = dormant; layout(); if (was && !dormant) go(Math.min(idx, maxIdx), false); },
    };
    if (sid) mains[sid] = api;
    allApis.push(api);
    el.__dlSlider = api;

    layout();
    go(0, false);

    if (window.ResizeObserver) {
      try {
        var ro = new ResizeObserver(safe(function () { if (dormant && el.clientWidth > 0) api.relayout(); }));
        ro.observe(el);
      } catch (e) {}
    }
    // auto-width slides depend on image intrinsic size: re-measure once images finish
    if (auto) {
      $all('img', el).forEach(function (img) {
        if (img.complete) return;
        img.addEventListener('load', safe(function () { api.relayout(); }), { once: true });
      });
    }
    return api;
  }

  function initSliders(root) {
    $all('[data-instant-type="slider"]', root).forEach(function (el) { safe(initSlider)(el); });
  }

  /* ================= accordions ================= */
  function contentOf(item) { return item.querySelector('[data-instant-type="accordion-content"]'); }
  function setAcc(item, open) {
    var c = contentOf(item);
    if (c) {
      c.style.setProperty('--instant-accordion-content-width', c.clientWidth + 'px');
      c.style.setProperty('--instant-accordion-content-height', open ? c.scrollHeight + 'px' : '0px');
    }
    item.setAttribute('data-state', open ? 'open' : 'closed');
    if (open) setTimeout(safe(function () { allApis.forEach(function (a) { if (a.isDormant()) a.relayout(); }); }), 60);
  }
  function initAccordions(root) {
    $all('[data-instant-type="accordion-container"]', root).forEach(function (container) {
      if (bound(container, '__dlAcc')) return;
      var items = $all('[data-instant-type="accordion-item"]', container).filter(function (i) { return closest(i, '[data-instant-type="accordion-container"]') === container; });
      var multi = container.getAttribute('data-is-multi-open-enabled') === 'true';
      items.forEach(function (item) {
        var c = contentOf(item);
        if (c) { c.style.setProperty('--instant-accordion-content-height', '0'); c.style.setProperty('--instant-accordion-content-width', c.clientWidth + 'px'); }
        if (item.getAttribute('data-state') !== 'open') item.setAttribute('data-state', 'closed');
        var header = item.querySelector('[data-instant-type="accordion-header"]');
        if (!header || bound(header, '__dlAccH')) return;
        header.addEventListener('click', safe(function (e) {
          e.preventDefault(); e.stopPropagation();
          var open = item.getAttribute('data-state') !== 'open';
          if (open && !multi) items.forEach(function (o) { if (o !== item) setAcc(o, false); });
          setAcc(item, open);
        }));
      });
    });
  }

  /* ================= overlays (the early-access waitlist pop-up) ================= */
  var savedOverflow = null;
  function overlayFor(id) { return document.getElementById('i' + id) || document.getElementById(id); }
  function openOverlay(o) {
    if (!o || o.getAttribute('data-state') === 'open') return;
    if (savedOverflow === null) savedOverflow = document.body.style.overflow;
    o.setAttribute('data-state', 'open');
    document.body.style.overflow = 'hidden';
    var f = o.querySelector('button');
    if (f) { try { f.focus({ preventScroll: true }); } catch (e) {} }
  }
  function closeOverlay(o) {
    if (!o) return;
    o.setAttribute('data-state', 'closed');
    $all('video', o).forEach(function (v) { try { v.pause(); } catch (e) {} });
    if (!document.querySelector('[data-instant-type="overlay"][data-state="open"]')) {
      document.body.style.overflow = savedOverflow || '';
      savedOverflow = null;
    }
  }
  function initOverlays(root) {
    $all('[data-instant-action-type="open-overlay"]', root).forEach(function (btn) {
      if (bound(btn, '__dlOv')) return;
      btn.addEventListener('click', safe(function (e) {
        e.preventDefault(); e.stopPropagation();
        var id = btn.getAttribute('data-instant-action-id');
        if (!id) { closeOverlay(closest(btn, '[data-instant-type="overlay"]')); return; }
        var o = overlayFor(id);
        // a trigger whose overlay is not on the page (the waitlist section left out of a template) does nothing
        if (!o) return;
        if (closest(btn, '[data-instant-type="overlay"]') === o && o.getAttribute('data-state') === 'open') closeOverlay(o);
        else openOverlay(o);
      }));
    });
    $all('[data-instant-type="overlay"]', root).forEach(function (o) {
      if (bound(o, '__dlOvRoot')) return;
      o.addEventListener('pointerdown', safe(function (e) { if (e.target === o) closeOverlay(o); }));
    });
    if (!window.__dlEsc) {
      window.__dlEsc = true;
      document.addEventListener('keydown', safe(function (e) {
        if (e.key !== 'Escape') return;
        var open = $all('[data-instant-type="overlay"][data-state="open"]');
        if (open.length) closeOverlay(open[open.length - 1]);
      }));
    }
  }

  /* ================= scroll triggers (the sticky "Shop Moasure LX1" bar) ================= */
  function initScrollTriggers(root) {
    var els = $all('.instant-scroll-trigger', root).filter(function (el) { return !bound(el, '__dlScroll'); });
    if (!els.length) return;
    var ticking = false;
    function evaluate() {
      ticking = false;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      els.forEach(function (el) {
        var offset = parseInt(el.getAttribute('data-instant-scroll-into-view-offset') || '0', 10) || 0;
        var replay = el.getAttribute('data-instant-scroll-into-view-replay') === 'true';
        // instant-core evaluates ONLY on scroll events (never at load), showing when scrollY >= offset; with
        // replay off it never re-hides. Measured on this page's sticky bar (offset 800, replay false), 21 Oct 2026:
        // hidden at load, shown from 800 px, still shown after scrolling back to 100 px.
        var shown = y >= offset;
        if (!shown && !replay && el.__dlShown) return;
        if (shown) el.__dlShown = true;
        el.classList.toggle('instant-scroll-trigger--hidden', !shown);
      });
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; window.requestAnimationFrame(evaluate); } }, { passive: true });
    // no evaluate() at load — the markup's initial `instant-scroll-trigger--hidden` state stands until the first scroll
  }

  /* ================= tickers (the two logo marquees) =================
   * As instant-core 5.0.x does (measured on the homepage hero, assets/home-hero-runtime.js): copy the logos of the
   * first `.instant-ticker` into the empty second one and write the variables the stylesheet's keyframes read —
   * --gap (host column-gap), --duration (track width / data-instant-ticker-speed, in s), --direction,
   * --pause-on-hover, --play, --multiplier. Redone on resize: the gap differs between breakpoints.
   */
  function tracksOf(host) { return kids(host, 'instant-ticker'); }
  function measureTicker(host) {
    var tracks = tracksOf(host);
    if (!tracks.length) return;
    var speed = parseFloat(host.getAttribute('data-instant-ticker-speed')) || 24;
    host.style.setProperty('--gap', getComputedStyle(host).columnGap || '0px');
    host.style.setProperty('--play', 'running');
    host.style.setProperty('--direction', host.getAttribute('data-instant-ticker-is-reversed') === 'true' ? 'reverse' : 'normal');
    host.style.setProperty('--duration', (tracks[0].scrollWidth / speed) + 's');
    host.style.setProperty('--pause-on-hover', host.getAttribute('data-instant-ticker-pause') === 'true' ? 'paused' : 'running');
    host.style.setProperty('--multiplier', '1');
  }
  var tickers = [];
  function initTickers(root) {
    $all('[data-instant-type="ticker"]', root).forEach(function (host) {
      if (bound(host, '__dlTicker')) return;
      var tracks = tracksOf(host);
      if (tracks.length < 2) return;
      var seed = tracks[0].querySelector('.instant-ticker-initial-child-container');
      if (seed && !tracks[1].children.length) {
        kids(seed).forEach(function (c) {
          var copy = c.cloneNode(true);
          $all('img', copy).forEach(function (i) { i.setAttribute('alt', ''); });
          tracks[1].appendChild(copy);
        });
        tracks[1].setAttribute('aria-hidden', 'true');
      }
      measureTicker(host);
      tickers.push(host);
      // images arriving late change the track width, and with it the duration
      $all('img', tracks[0]).forEach(function (i) { if (!i.complete) i.addEventListener('load', safe(function () { measureTicker(host); }), { once: true }); });
    });
  }

  /* ================= resize ================= */
  function initResize() {
    if (window.__dlResize) return;
    window.__dlResize = true;
    var t = null;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(safe(function () {
        allApis.forEach(function (s) { s.layout(); s.go(s.index(), false); });
        $all('[data-instant-type="accordion-item"][data-state="open"]').forEach(function (item) { setAcc(item, true); });
        tickers.forEach(measureTicker);
      }), 100);
    });
  }

  /* ================= mode decision ================= */
  function coreRan() {
    var I = window.Instant;
    if (I && (typeof I.initSwiper === 'function' || I.initializedListeners || I.eventAbortController || I.api)) return true;
    return !!document.querySelector('[data-instant-slider-initialized="true"]');
  }
  function standDown() { window.__discoverLx1Mode = 'instant'; }
  function takeOver() {
    window.__discoverLx1Mode = 'native';
    $all(ROOT_SEL).forEach(function (root) {
      safe(initSliders)(root);
      safe(initAccordions)(root);
      safe(initTickers)(root);
      safe(initOverlays)(root);
      safe(initScrollTriggers)(root);
    });
    safe(initResize)();
    window.__discoverLx1NativeReady = true;
    try { window.dispatchEvent(new Event('discover-lx1:ready')); } catch (e) {}
  }
  function decide() {
    if (coreRan()) return standDown();
    if (!document.querySelector('script[src*="instant-core"]')) return takeOver();
    var tries = 0;
    (function poll() {
      if (coreRan()) return standDown();
      if (++tries > 15) return takeOver();   // ~1.5s: the tag is present but blocked/failed
      setTimeout(poll, 100);
    })();
  }

  decide();
})();
